import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  asyncScheduler,
  catchError,
  combineLatest,
  concatMap,
  debounceTime,
  delayWhen,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  fromEvent,
  map,
  mergeMap,
  Observable,
  observeOn,
  of,
  skipWhile,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs';

import {
  PageContextFacade,
  runtimeActions,
  runtimeSelectors,
} from '@blaze-runtime/engine-data-access';
import { logPageLayout } from '@blaze-runtime/engine-util';
import {
  appStateActions,
  blazeActions,
  ProjectContextFacade,
  RuntimeMode,
} from '@blaze-shared/app-state-data-access';
import { CanvasElementDataProperties } from '@blaze-shared/elements-domain';
import { APP_READY_INFO_URL_PARAM } from '@blaze-shared/projects-domain';
import { DatasetSchema } from '@blaze-shared/schema-domain';
import { mutationRxUtils, performanceRxUtils } from '@blaze-shared/shared-util';
import { VariablesFacade } from '@blaze-shared/variables-data-access';
import { WorkflowsApiService } from '@blaze-shared/workflow-data-access';
import { Workflow, WorkflowPhase } from '@blaze-shared/workflow-domain';

import { LayoutGenerationService } from '../../services/layout-generation.service';
import { LayoutLoadingService } from '../../services/layout-loading.service';
import { LayoutLockService } from '../../services/layout-lock.service';
import { RuntimeService } from '../../services/runtime.service';
import {
  initializeLayout,
  layoutFetched,
  layoutInitialized,
  layoutReady,
  updateElementProps,
} from '../elements/elements.actions';
import { RuntimeFacade } from '../runtime/runtime.facade';

@Injectable()
export class EngineEffects {
  private readonly actions$ = inject(Actions);
  private readonly layoutGenerationService = inject(LayoutGenerationService);
  private readonly layoutLoading = inject(LayoutLoadingService);
  private readonly layoutLockService = inject(LayoutLockService);
  private readonly pageContextFacade = inject(PageContextFacade);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly runtime = inject(RuntimeService);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly store = inject(Store);
  private readonly variablesFacade = inject(VariablesFacade);
  private readonly workflowsApi = inject(WorkflowsApiService);

  /**
   * Layout elements should be generated only when in editor mode.
   */
  layoutFetched$ = createEffect(() =>
    this.actions$.pipe(
      ofType(layoutFetched),
      concatLatestFrom(({ layout }) => [
        this.store.select(runtimeSelectors.selectRuntimeMode),
        this.store.select(runtimeSelectors.selectPageById(layout.pageId)),
      ]),
      mergeMap(([{ layout }, runtimeMode, page]) => {
        if (page?.pageType === 'Page' && runtimeMode === RuntimeMode.Editor) {
          return [initializeLayout({ layout })];
        }

        logPageLayout('EngineEffects', 'layoutFetched$', (tag) => {
          console.log(`${tag}: Skipping layout generation for page ${layout.pageId}`);
        });
        return [layoutInitialized({ layout })];
      }),
    ),
  );

  /**
   * Every page layout should always be populated with at least a root element.
   */
  normalizeLayout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(layoutInitialized),
      concatLatestFrom(({ layout }) =>
        this.store.select(runtimeSelectors.selectPageById(layout.pageId)),
      ),
      mergeMap(([{ layout, elements }, page]) => {
        if (elements?.length) {
          return [layoutReady({ layout, elements })];
        }
        return [
          layoutReady({
            layout,
            elements: this.layoutGenerationService.normalizeLayout(layout, page),
          }),
        ];
      }),
    ),
  );

  updateSchemasForLayout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(layoutReady),
      mergeMap(({ layout }) =>
        this.runtime.getSchemasForElements(layout.uiLayer.content).pipe(
          map((schemas) => runtimeActions.schemasUpdateForLayout({ schemas, layout })),
          catchError((err) => {
            console.error(`Could not update data schemas for layout ${layout.pageId}`, err);
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  loadProjectRuntime$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appStateActions.loadProjectRuntime),
      switchMap(({ projectId }) => {
        const currAccountName = this.projectContextFacade.varAccount?.Name;
        return combineLatest({
          storedVars: this.variablesFacade.initVariables(projectId, currAccountName),
          workflowsAndSchemas: this.fetchWorkflowsAndSchemas(projectId),
          // When initially loading the page, prepopulate the page context
          // Page ID is known at this point (initialized)
          pageContext: this.pageContextFacade.loadPageContextForProject(projectId),
        }).pipe(
          map(({ workflowsAndSchemas }) =>
            runtimeActions.runtimeInfoFetched({
              projectId,
              schemas: workflowsAndSchemas.schemas,
              workflows: workflowsAndSchemas.workflows,
            }),
          ),
          catchError((err) => {
            console.error(`Could not fetch data for project ${projectId}`, err);
            return EMPTY;
          }),
        );
      }),
    ),
  );

  elementBindingsChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateElementProps),
      filter(({ path }) => path === 'data'),
      mergeMap(({ elementId, value }) =>
        this.runtime.getSchemasFromBindings(value as CanvasElementDataProperties[]).pipe(
          map((schemas) => runtimeActions.schemasUpdate({ schemas })),
          catchError((err) => {
            console.error(`Could not update data schemas for element ${elementId}`, err);
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  /**
   * Waits for the page's layout to get loaded and starts the runtime
   *
   * **Important:** the logic below relies on the editor or preview mode
   * which should render the blaze-canvas element with the proper pageId and elements
   */
  waitForRuntimePageSet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(blazeActions.setRuntimePage),
      switchMap(({ projectId, pageId }) =>
        combineLatest({
          loadedProjectId: this.runtimeFacade.loadedProjectId$,
          loadedLayoutIds: this.layoutLoading.loadedLayoutIds$,
          loadingLayoutIds: this.layoutLoading.waitingForIds$,
        }).pipe(
          // Wait until the page's layouts get loaded
          skipWhile(
            ({ loadedProjectId, loadedLayoutIds, loadingLayoutIds }) =>
              loadedProjectId !== projectId ||
              loadedLayoutIds.length === 0 ||
              loadingLayoutIds.length > 0 ||
              !loadedLayoutIds.includes(pageId),
          ),
          take(1),

          // Wait for the layout to be fully rendered
          delayWhen(() => this.layoutLockService.unlocked$),

          // Wait for all other code to execute before emitting the start action
          observeOn(asyncScheduler),

          map(() => blazeActions.startRuntime({ projectId, pageId })),

          // Dispatch a custom event to signal that the runtime is ready to hide the loader (BL-8212)
          tap(() => document.dispatchEvent(new CustomEvent('blazeRuntimePageSet'))),
        ),
      ),
    ),
  );

  /**
   * If `appReadyInfo` param is enabled in the URL, show a special console.log message when the app and content are ready
   * Used for Print to PDF feature (in Puppeteer).
   */
  initialLoadCompleted$ = createEffect(
    () =>
      of(new URL(window.location.href).searchParams.get(APP_READY_INFO_URL_PARAM)).pipe(
        concatMap((appReadyInfo) => {
          // Feature not active
          if (appReadyInfo !== 'true') {
            return EMPTY;
          }

          // Prepare and monitor the conditions
          return combineLatest({
            // Wait for the document to be ready
            isDocumentReady: fromEvent(document, 'readystatechange').pipe(
              startWith(undefined), // Make sure something gets emitted initially
              map(() => document.readyState === 'complete'),
            ),
            // Layout loading progress
            hasLayoutsLoaded: this.layoutLoading.loadedLayoutIds$.pipe(
              map((ids) => ids.length > 0),
            ),
            hasLayoutsLoading: this.layoutLoading.waitingForIds$.pipe(map((ids) => ids.length > 0)),
            // Runtime state
            isRunning: this.store.select(runtimeSelectors.selectIsRunning),
            isRuntimeInfoFetched: this.store.select(runtimeSelectors.selectIsRuntimeInfoFetched),
            // Data loading
            hasNotEmptyConnectionChains: this.store
              .select(runtimeSelectors.selectConnectionChains)
              .pipe(map((chains) => Object.values(chains).flat().length > 0)),
            hasPendingPaginations: this.store
              .select(runtimeSelectors.selectPendingPaginations)
              .pipe(map((paginations) => Object.keys(paginations).length > 0)),
            hasPendingUi: this.store
              .select(runtimeSelectors.selectPendingUi)
              .pipe(map((pendingUi) => Object.keys(pendingUi).length > 0)),
            hasRunningWorkflows: this.store
              .select(runtimeSelectors.selectWorkflowsStatus)
              .pipe(
                map((workflowsStatus) =>
                  Object.values(workflowsStatus).some(
                    (status) => status?.phase === WorkflowPhase.Running,
                  ),
                ),
              ),
            // Make sure all resources (CSS, Fonts, JS, IMGs) are ready
            allResourcesReady: performanceRxUtils
              .fromPerformanceObserver({ entryTypes: ['resource'] })
              .pipe(
                // Make sure something gets emitted initially
                startWith([]),
                // Always check all resources
                map(() =>
                  performance
                    .getEntriesByType('resource')
                    .every(
                      (entry) =>
                        entry instanceof PerformanceResourceTiming &&
                        entry.startTime > 0 &&
                        entry.responseEnd > 0,
                    ),
                ),
              ),
            isLayoutUnlocked: this.layoutLockService.lock$.pipe(map((isLocked) => !isLocked)),
            // Elements that render asynchronously after mount (e.g. QR code canvases)
            // carry a data-print-pending attribute until they finish painting. Their
            // completion is DOM-only (no store/network activity), hence the
            // MutationObserver — it catches even sub-frame pending windows that
            // interval polling would miss.
            hasPendingPrintElements: mutationRxUtils
              .fromMutationObserver(document.body, {
                subtree: true,
                childList: true,
                attributes: true,
                attributeFilter: ['data-print-pending'],
              })
              .pipe(
                catchError(() => EMPTY),
                startWith([]),
                map(() => !!document.querySelector('[data-print-pending]')),
                distinctUntilChanged(),
              ),
          }).pipe(
            // Check if all conditions are met
            map(
              ({
                isDocumentReady,
                hasLayoutsLoaded,
                hasLayoutsLoading,
                isRunning,
                isRuntimeInfoFetched,
                hasNotEmptyConnectionChains,
                hasPendingPaginations,
                hasPendingUi,
                hasRunningWorkflows,
                allResourcesReady,
                isLayoutUnlocked,
                hasPendingPrintElements,
              }) =>
                isDocumentReady &&
                hasLayoutsLoaded &&
                !hasLayoutsLoading &&
                isRunning &&
                isRuntimeInfoFetched &&
                !hasNotEmptyConnectionChains &&
                !hasPendingPaginations &&
                !hasPendingUi &&
                !hasRunningWorkflows &&
                allResourcesReady &&
                isLayoutUnlocked &&
                !hasPendingPrintElements,
            ),
            debounceTime(1000), // If no changes coming, wait for a second to make sure everything settled (account for debounces and such)
            first((isAppAndContentReady) => isAppAndContentReady === true), // Emit only once
            tap(() => {
              // This is used by the Print to PDF feature and should not be modified
              console.log('App and content ready.');
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  private fetchWorkflowsAndSchemas(
    projectId: string,
  ): Observable<{ workflows: Workflow[]; schemas: Record<string, DatasetSchema> }> {
    return this.workflowsApi.getForProject(projectId).pipe(
      concatMap((workflows) =>
        combineLatest({
          workflows: of(workflows),
          schemas: this.runtime.getSchemasForWorkflows(workflows),
        }),
      ),
    );
  }
}
